// ==============================================
// AGENTE IA DE SEMANA DE AVALIAÇÕES
// DNACore - Sistema de Gestão Escolar
// Interface Premium de Planejamento Pedagógico
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Brain, Sparkles, AlertTriangle, CheckCircle2, 
  Clock, Users, BookOpen, FileText, Settings, RefreshCw,
  Download, Send, Eye, Trash2, Plus, ChevronRight, Filter,
  Loader2, XCircle, Info, Zap, Target, TrendingUp, BarChart3,
  Check, List, Printer, GraduationCap, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = window.location.origin || '';

// ==============================================
// COMPONENTES AUXILIARES
// ==============================================

const StatCard = ({ title, value, icon: Icon, color = "purple", trend }) => (
  <Card className="relative overflow-hidden">
    <div className={`absolute inset-0 bg-gradient-to-br from-${color}-500/10 to-transparent`} />
    <CardContent className="p-6 relative">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-3xl font-bold mt-1">{value}</h3>
          {trend && (
            <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-600' : 'text-gray-500'}`}>
              {trend > 0 ? '+' : ''}{trend}% este trimestre
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }) => {
  const statusConfig = {
    rascunho: { color: 'bg-gray-100 text-gray-700', label: 'Rascunho' },
    gerado: { color: 'bg-blue-100 text-blue-700', label: 'Gerado' },
    revisado: { color: 'bg-yellow-100 text-yellow-700', label: 'Revisado' },
    aprovado: { color: 'bg-green-100 text-green-700', label: 'Aprovado' },
    publicado: { color: 'bg-purple-100 text-purple-700', label: 'Publicado' }
  };
  
  const config = statusConfig[status] || statusConfig.rascunho;
  
  return (
    <Badge className={`${config.color} font-medium`}>
      {config.label}
    </Badge>
  );
};

const PesoBadge = ({ peso }) => {
  const pesoConfig = {
    leve: { color: 'bg-green-100 text-green-700', label: 'Leve' },
    media: { color: 'bg-yellow-100 text-yellow-700', label: 'Média' },
    pesada: { color: 'bg-red-100 text-red-700', label: 'Pesada' }
  };
  
  const config = pesoConfig[peso] || pesoConfig.media;
  
  return (
    <Badge variant="outline" className={`${config.color} text-xs`}>
      {config.label}
    </Badge>
  );
};

const ConflitoBadge = ({ severidade }) => {
  const config = {
    baixa: { color: 'bg-blue-100 text-blue-700', icon: Info },
    media: { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
    alta: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
    critica: { color: 'bg-red-100 text-red-700', icon: XCircle }
  };
  
  const c = config[severidade] || config.media;
  const Icon = c.icon;
  
  return (
    <Badge className={`${c.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {severidade?.toUpperCase()}
    </Badge>
  );
};

// ==============================================
// CORES DAS DISCIPLINAS
// ==============================================
const CORES_DISCIPLINAS = {
  'Matemática': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  'Português': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'Língua Portuguesa': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'História': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
  'Geografia': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  'Ciências': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
  'Física': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  'Química': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  'Biologia': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  'Inglês': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  'Espanhol': { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-200' },
  'Artes': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-200' },
  'Arte': { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-200' },
  'Educação Física': { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-200' },
  'Filosofia': { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-200' },
  'Sociologia': { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-200' },
  'Programa Bilíngue': { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-200' },
  'Produção Textual': { bg: 'bg-lime-100', text: 'text-lime-800', border: 'border-lime-200' },
  'Educação Financeira': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
};

const getCorDisciplina = (nome) => {
  return CORES_DISCIPLINAS[nome] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };
};

// ==============================================
// COMPONENTE DE GRADE POR TURMA (PREMIUM)
// ==============================================
const GradeAvaliacoesPorTurma = ({ plano, avaliacoes, porData, onEditarAvaliacao, onDeletarAvaliacao, onRefresh }) => {
  const [turmaAtual, setTurmaAtual] = useState(null);
  const [viewMode, setViewMode] = useState('grade'); // 'grade' ou 'lista'
  
  // Organizar avaliações por turma
  const turmasUnicas = React.useMemo(() => {
    const turmasMap = new Map();
    avaliacoes.forEach(aval => {
      if (!turmasMap.has(aval.turma_id)) {
        turmasMap.set(aval.turma_id, {
          id: aval.turma_id,
          nome: aval.turma_nome,
          avaliacoes: []
        });
      }
      turmasMap.get(aval.turma_id).avaliacoes.push(aval);
    });
    return Array.from(turmasMap.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [avaliacoes]);

  // Definir turma atual
  React.useEffect(() => {
    if (turmasUnicas.length > 0 && !turmaAtual) {
      setTurmaAtual(turmasUnicas[0]);
    }
  }, [turmasUnicas, turmaAtual]);

  // Organizar dias e horários
  const diasSemana = React.useMemo(() => {
    const dias = {};
    Object.keys(porData).sort().forEach(data => {
      const d = new Date(data + 'T12:00:00');
      const diaSemana = d.toLocaleDateString('pt-BR', { weekday: 'long' });
      const diaFormatado = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      dias[data] = { 
        diaSemana: diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1),
        diaFormatado,
        date: d
      };
    });
    return dias;
  }, [porData]);

  // Obter horários únicos ordenados
  const horariosUnicos = React.useMemo(() => {
    const horarios = new Set();
    avaliacoes.forEach(aval => {
      if (aval.horario_inicio && aval.horario_fim) {
        horarios.add(`${aval.horario_inicio}-${aval.horario_fim}`);
      }
    });
    return Array.from(horarios).sort();
  }, [avaliacoes]);

  // Montar grid de avaliações para a turma atual
  const gridTurma = React.useMemo(() => {
    if (!turmaAtual) return {};
    
    const grid = {};
    horariosUnicos.forEach(horario => {
      grid[horario] = {};
      Object.keys(diasSemana).forEach(data => {
        grid[horario][data] = null;
      });
    });

    turmaAtual.avaliacoes.forEach(aval => {
      const horario = `${aval.horario_inicio}-${aval.horario_fim}`;
      if (grid[horario] && grid[horario].hasOwnProperty(aval.data_avaliacao)) {
        grid[horario][aval.data_avaliacao] = aval;
      }
    });

    return grid;
  }, [turmaAtual, horariosUnicos, diasSemana]);

  const CORES_HEADER = [
    'bg-gradient-to-r from-orange-500 to-orange-400',
    'bg-gradient-to-r from-pink-500 to-pink-400',
    'bg-gradient-to-r from-green-500 to-green-400',
    'bg-gradient-to-r from-amber-500 to-amber-400',
    'bg-gradient-to-r from-cyan-500 to-cyan-400',
  ];

  if (turmasUnicas.length === 0) {
    return (
      <Card className="py-12 text-center">
        <CardContent>
          <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600">Nenhuma avaliação encontrada</h3>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Seletor de Turma e Modo */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {turmasUnicas.map(turma => (
            <Button
              key={turma.id}
              variant={turmaAtual?.id === turma.id ? "default" : "outline"}
              size="sm"
              onClick={() => setTurmaAtual(turma)}
              className={turmaAtual?.id === turma.id ? "bg-purple-600 hover:bg-purple-700" : ""}
            >
              <Users className="h-4 w-4 mr-1" />
              {turma.nome}
              <Badge variant="secondary" className="ml-2 bg-white/20">
                {turma.avaliacoes.length}
              </Badge>
            </Button>
          ))}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grade' ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode('grade')}
            className={viewMode === 'grade' ? "bg-purple-600" : ""}
          >
            <Calendar className="h-4 w-4 mr-1" />
            Grade
          </Button>
          <Button
            variant={viewMode === 'lista' ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode('lista')}
            className={viewMode === 'lista' ? "bg-purple-600" : ""}
          >
            <FileText className="h-4 w-4 mr-1" />
            Lista
          </Button>
        </div>
      </div>

      {turmaAtual && viewMode === 'grade' && (
        <Card className="overflow-hidden shadow-xl">
          {/* Header Premium */}
          <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 text-white p-6 text-center">
            <h2 className="text-2xl font-bold tracking-wide">{turmaAtual.nome}</h2>
            <p className="text-purple-200 mt-1">
              Semana de Avaliações - {plano?.trimestre}º Trimestre {new Date().getFullYear()}
            </p>
          </div>

          {/* Tabela Grade */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="bg-gray-100 text-gray-700 font-semibold py-3 px-4 text-left w-32">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Horário
                    </div>
                  </th>
                  {Object.entries(diasSemana).map(([data, info], idx) => (
                    <th 
                      key={data}
                      className={`${CORES_HEADER[idx % CORES_HEADER.length]} text-white font-semibold py-3 px-4 text-center min-w-[150px]`}
                    >
                      <div>{info.diaSemana}</div>
                      <div className="text-xs font-normal opacity-90">{info.diaFormatado}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {horariosUnicos.map((horario, rowIdx) => {
                  const [inicio, fim] = horario.split('-');
                  return (
                    <tr key={horario} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-4 px-4 font-medium text-gray-600 border-r border-gray-200">
                        <div className="text-sm">{inicio}</div>
                        <div className="text-xs text-gray-400">{fim}</div>
                      </td>
                      {Object.keys(diasSemana).map(data => {
                        const aval = gridTurma[horario]?.[data];
                        const cor = aval ? getCorDisciplina(aval.disciplina_nome) : null;
                        
                        return (
                          <td key={data} className="p-2 border border-gray-100">
                            {aval ? (
                              <div 
                                className={`${cor.bg} ${cor.border} border rounded-lg p-3 text-center transition-all hover:shadow-md relative group`}
                              >
                                <p className={`font-semibold ${cor.text}`}>{aval.disciplina_nome}</p>
                                {aval.professor_nome && (
                                  <p className="text-xs text-gray-600 mt-1 truncate" title={aval.professor_nome}>
                                    {aval.professor_nome}
                                  </p>
                                )}
                                <div className="mt-2">
                                  <PesoBadge peso={aval.peso_disciplina} />
                                </div>
                                {/* Botões de ação */}
                                {(onEditarAvaliacao || onDeletarAvaliacao) && (
                                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    {onEditarAvaliacao && (
                                      <button 
                                        onClick={() => onEditarAvaliacao(aval)}
                                        className="p-1 bg-white rounded shadow hover:bg-blue-50"
                                        title="Editar"
                                      >
                                        <Settings className="h-3 w-3 text-blue-600" />
                                      </button>
                                    )}
                                    {onDeletarAvaliacao && (
                                      <button 
                                        onClick={() => onDeletarAvaliacao(aval.id)}
                                        className="p-1 bg-white rounded shadow hover:bg-red-50"
                                        title="Excluir"
                                      >
                                        <Trash2 className="h-3 w-3 text-red-600" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="h-full min-h-[80px] flex items-center justify-center text-gray-300">
                                —
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer com Legenda */}
          <div className="bg-gray-50 p-4 border-t">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-gray-500 font-medium">Legenda de Peso:</span>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-green-200"></span>
                <span className="text-gray-600">Leve</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-yellow-200"></span>
                <span className="text-gray-600">Média</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-200"></span>
                <span className="text-gray-600">Pesada</span>
              </div>
              <div className="ml-auto text-gray-500">
                Total: <strong>{turmaAtual.avaliacoes.length}</strong> avaliações
              </div>
            </div>
          </div>
        </Card>
      )}

      {turmaAtual && viewMode === 'lista' && (
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-700 to-indigo-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {turmaAtual.nome} - Lista de Avaliações
            </CardTitle>
            <CardDescription className="text-purple-200">
              {turmaAtual.avaliacoes.length} avaliações programadas
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid gap-3">
              {turmaAtual.avaliacoes
                .sort((a, b) => new Date(a.data_avaliacao) - new Date(b.data_avaliacao))
                .map((aval) => {
                  const cor = getCorDisciplina(aval.disciplina_nome);
                  return (
                    <div 
                      key={aval.id}
                      className={`flex items-center justify-between p-4 ${cor.bg} ${cor.border} border rounded-lg hover:shadow-md transition-all`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center min-w-[80px] bg-white rounded-lg p-2 shadow-sm">
                          <p className="text-xs text-gray-500">
                            {new Date(aval.data_avaliacao + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'short' })}
                          </p>
                          <p className="font-bold text-lg">{new Date(aval.data_avaliacao + 'T12:00:00').getDate()}</p>
                          <p className="text-xs text-gray-500">{aval.horario_inicio}</p>
                        </div>
                        <div>
                          <p className={`font-semibold text-lg ${cor.text}`}>{aval.disciplina_nome}</p>
                          {aval.professor_nome && (
                            <p className="text-sm text-gray-600">{aval.professor_nome}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <ExamFileButton avalId={aval.id} examFile={aval.exam_file} onUploaded={() => onRefresh?.()} />
                        <PesoBadge peso={aval.peso_disciplina} />
                        {aval.status_validacao === 'valido' && (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Válido
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==============================================
// BOTÃO UPLOAD/IMPRESSÃO DE PROVA
// ==============================================
const ExamFileButton = ({ avalId, examFile, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/assessment-week/items/${avalId}/exam-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Prova enviada!');
      if (onUploaded) onUploaded();
    } catch { toast.error('Erro ao enviar prova'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (examFile?.file_url) {
    return (
      <div className="flex items-center gap-1">
        <a href={`${API_URL}${examFile.file_url}`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 text-[10px] font-medium transition-colors">
          <Eye className="h-3 w-3" /> Ver prova
        </a>
        <label className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-200 text-[10px] font-medium cursor-pointer transition-colors">
          <RefreshCw className="h-3 w-3" /> Trocar
          <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg" onChange={handleUpload} ref={fileRef} />
        </label>
      </div>
    );
  }

  return (
    <label className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[10px] font-medium cursor-pointer transition-colors ${uploading ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200'}`}>
      {uploading ? <><Loader2 className="h-3 w-3 animate-spin" /> Enviando...</> : <><Plus className="h-3 w-3" /> Enviar prova</>}
      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg" onChange={handleUpload} disabled={uploading} ref={fileRef} />
    </label>
  );
};

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================

export default function AssessmentWeek() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [planoAtual, setPlanoAtual] = useState(null);
  const [turmas, setTurmas] = useState({ turmas: [], por_segmento: {}, total: 0 });
  const [conflitos, setConflitos] = useState([]);
  const [regras, setRegras] = useState(null);
  
  // Estado do formulário de geração
  const [geracaoForm, setGeracaoForm] = useState({
    trimestre: 1,
    segmento: '',
    turma_ids: [],
    data_inicio: '',
    data_fim: '',
    modo: 'equilibrado',
    gerar_todas_turmas: false,
    gerar_todas_disciplinas: true,
    disciplinas_selecionadas: {}  // { turma_id: [disciplina1, disciplina2, ...] }
  });
  
  const [showNovaGeracao, setShowNovaGeracao] = useState(false);
  const [showPublicar, setShowPublicar] = useState(false);
  const [showEditarAvaliacao, setShowEditarAvaliacao] = useState(false);
  const [showAdicionarAvaliacao, setShowAdicionarAvaliacao] = useState(false);
  const [avaliacaoEditando, setAvaliacaoEditando] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [salvandoAvaliacao, setSalvandoAvaliacao] = useState(false);
  const [disciplinasPorTurma, setDisciplinasPorTurma] = useState({});  // Cache de disciplinas
  const [loadingDisciplinas, setLoadingDisciplinas] = useState(false);
  
  // Estado para exportar/imprimir
  const [showExportarDialog, setShowExportarDialog] = useState(false);
  // V4.93 · Publicar cronograma no Google Classroom
  const [showClassroomDialog, setShowClassroomDialog] = useState(false);
  const [classroomFiltroAno, setClassroomFiltroAno] = useState(new Date().getFullYear());
  const [classroomFiltroTri, setClassroomFiltroTri] = useState(1);
  const [classroomTurmas, setClassroomTurmas] = useState([]);
  const [classroomSelected, setClassroomSelected] = useState(() => new Set());
  const [classroomLoading, setClassroomLoading] = useState(false);
  const [classroomPublishing, setClassroomPublishing] = useState(false);
  const [classroomResults, setClassroomResults] = useState(null);
  const [classroomForceReplace, setClassroomForceReplace] = useState(false);

  const loadClassroomPreview = useCallback(async () => {
    setClassroomLoading(true);
    setClassroomResults(null);
    try {
      const { data } = await api.get('/assessment-week/classroom/preview', {
        params: { ano: classroomFiltroAno, trimestre: classroomFiltroTri }
      });
      setClassroomTurmas(data.turmas || []);
      // Pré-seleciona somente as com sala vinculada E não publicadas
      const preSel = new Set(
        (data.turmas || [])
          .filter(t => t.classroom_course_id && !t.already_published)
          .map(t => t.turma_id)
      );
      setClassroomSelected(preSel);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Falha ao carregar turmas.');
      setClassroomTurmas([]);
    } finally { setClassroomLoading(false); }
  }, [classroomFiltroAno, classroomFiltroTri]);

  useEffect(() => {
    if (showClassroomDialog) loadClassroomPreview();
  }, [showClassroomDialog, loadClassroomPreview]);

  const doClassroomPublish = async () => {
    const ids = Array.from(classroomSelected);
    if (!ids.length) { toast.error('Selecione ao menos uma turma.'); return; }
    // Confirmação especial para republicação
    const willRepublish = classroomTurmas.some(t => classroomSelected.has(t.turma_id) && t.already_published);
    if (willRepublish && classroomForceReplace) {
      const ok = window.confirm('Você está REPUBLICANDO cronogramas já enviados. Um novo post será criado no Classroom (o antigo permanece). Continuar?');
      if (!ok) return;
    } else if (willRepublish && !classroomForceReplace) {
      toast.error('Marque "Republicar cronograma atualizado" para reenviar turmas já publicadas.');
      return;
    }
    setClassroomPublishing(true);
    try {
      const { data } = await api.post('/assessment-week/classroom/publish', {
        class_group_ids: ids, ano: classroomFiltroAno, trimestre: classroomFiltroTri,
        force_replace: classroomForceReplace,
      });
      setClassroomResults(data);
      toast.success(`${data.success_count} turma(s) publicada(s). ${data.no_link_count} sem sala vinculada. ${data.error_count} falha(s).`);
      await loadClassroomPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Falha ao publicar no Classroom.');
    } finally { setClassroomPublishing(false); }
  };

  const doClassroomRetryErrors = async () => {
    if (!classroomResults) return;
    const failedIds = (classroomResults.results?.errors || []).map(x => x.turma_id);
    if (!failedIds.length) { toast.info('Nenhuma falha para tentar novamente.'); return; }
    setClassroomPublishing(true);
    try {
      const { data } = await api.post('/assessment-week/classroom/publish', {
        class_group_ids: failedIds, ano: classroomFiltroAno, trimestre: classroomFiltroTri,
        force_replace: classroomForceReplace,
      });
      setClassroomResults(data);
      toast.success(`Retry: ${data.success_count} sucesso · ${data.error_count} falha(s).`);
      await loadClassroomPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Falha no retry.');
    } finally { setClassroomPublishing(false); }
  };

  // V4.94 · Botão "Vincular Salas 2026" — carga oficial dos 30 course_ids
  const [bulkLinking, setBulkLinking] = useState(false);
  const doBulkLink2026 = async () => {
    const ok = window.confirm(
      'Vincular oficialmente as 30 salas do Google Classroom 2026 a cada turma?\n\n' +
      'Isso persiste `google_classroom_course_id` em class_groups conforme o mapeamento oficial. ' +
      'Turmas com ID diferente já cadastrado serão sinalizadas como CONFLITO (não sobrescreve silenciosamente).'
    );
    if (!ok) return;
    setBulkLinking(true);
    try {
      const { data } = await api.post('/assessment-week/classroom/bulk-link-2026');
      const parts = [
        `✓ ${data.linked_count} vinculadas`,
        `⚠ ${data.not_found_count} sem turma correspondente`,
      ];
      if (data.conflicts_count > 0) parts.push(`⚑ ${data.conflicts_count} conflitos`);
      toast.success(parts.join(' · '), { duration: 6000 });
      if (data.conflicts_count > 0) {
        console.warn('[bulk-link-2026] conflitos:', data.conflicts);
        toast.warning('Existem conflitos de vínculo. Veja o console para detalhes.', { duration: 8000 });
      }
      await loadClassroomPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Falha ao vincular salas.');
    } finally { setBulkLinking(false); }
  };
  const [exportarTipo, setExportarTipo] = useState('todas'); // 'todas' ou 'selecionadas'
  const [turmasSelecionadasExportar, setTurmasSelecionadasExportar] = useState(new Set());
  const [formatoImpressao, setFormatoImpressao] = useState('lista'); // 'lista' ou 'grade'
  const [todasTurmas, setTodasTurmas] = useState([]); // Todas as turmas do sistema
  const [exportarTrimestreFiltro, setExportarTrimestreFiltro] = useState(''); // Filtro por trimestre
  const [planosSelecionadosExportar, setPlanosSelecionadosExportar] = useState(new Set()); // Planos selecionados

  // Estado para nova avaliação manual
  const [novaAvaliacao, setNovaAvaliacao] = useState({
    turma_id: '',
    disciplina_nome: '',
    professor_nome: '',
    data_avaliacao: '',
    horario_inicio: '07:30',
    horario_fim: '08:20',
    aula_numero: 1
  });

  // ==============================================
  // FUNÇÕES DE API
  // ==============================================

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/dashboard`);
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  }, []);

  const fetchPlanos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/planos`);
      const data = await res.json();
      setPlanos(data.planos || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  }, []);

  const fetchTurmas = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/turmas`);
      const data = await res.json();
      setTurmas(data); // Armazenar objeto completo com turmas e por_segmento
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  }, []);

  const fetchConflitos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/conflitos?status=pendente`);
      const data = await res.json();
      setConflitos(data.conflitos || []);
    } catch (error) {
      console.error('Erro ao carregar conflitos:', error);
    }
  }, []);

  const ignorarConflito = async (conflitoId) => {
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/conflitos/${conflitoId}/ignorar`, {
        method: 'PUT'
      });
      
      if (res.ok) {
        toast.success('Conflito ignorado!');
        fetchConflitos();
        fetchDashboard();
      } else {
        toast.error('Erro ao ignorar conflito');
      }
    } catch (error) {
      console.error('Erro ao ignorar conflito:', error);
      toast.error('Erro ao ignorar conflito');
    }
  };

  const excluirConflito = async (conflitoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este conflito permanentemente?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/conflitos/${conflitoId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Conflito excluído!');
        fetchConflitos();
        fetchDashboard();
      } else {
        toast.error('Erro ao excluir conflito');
      }
    } catch (error) {
      console.error('Erro ao excluir conflito:', error);
      toast.error('Erro ao excluir conflito');
    }
  };

  const excluirTodosConflitos = async () => {
    if (!window.confirm('Tem certeza que deseja excluir TODOS os conflitos pendentes? Esta ação não pode ser desfeita.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/conflitos?status=pendente`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || 'Todos os conflitos foram excluídos!');
        fetchConflitos();
        fetchDashboard();
      } else {
        toast.error('Erro ao excluir conflitos');
      }
    } catch (error) {
      console.error('Erro ao excluir conflitos:', error);
      toast.error('Erro ao excluir conflitos');
    }
  };

  const fetchRegras = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/regras`);
      const data = await res.json();
      setRegras(data);
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    }
  }, []);

  const fetchTodasTurmas = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/class-groups`);
      const data = await res.json();
      setTodasTurmas(data || []);
    } catch (error) {
      console.error('Erro ao carregar todas as turmas:', error);
    }
  }, []);

  const fetchDisciplinasTurma = async (turmaId) => {
    // Se já temos em cache, não buscar novamente
    if (disciplinasPorTurma[turmaId]) return;
    
    try {
      setLoadingDisciplinas(true);
      const res = await fetch(`${API_URL}/api/assessment-week/turmas/${turmaId}/disciplinas`);
      const data = await res.json();
      
      setDisciplinasPorTurma(prev => ({
        ...prev,
        [turmaId]: data.disciplinas || []
      }));
      
      // Inicializar disciplinas selecionadas com todas (por padrão)
      setGeracaoForm(prev => ({
        ...prev,
        disciplinas_selecionadas: {
          ...prev.disciplinas_selecionadas,
          [turmaId]: (data.disciplinas || []).map(d => d.nome)
        }
      }));
    } catch (error) {
      console.error('Erro ao carregar disciplinas:', error);
    } finally {
      setLoadingDisciplinas(false);
    }
  };

  const salvarRegras = async () => {
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/regras`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regras)
      });
      
      if (res.ok) {
        toast.success('Configurações salvas com sucesso!');
        fetchRegras();
      } else {
        toast.error('Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar regras:', error);
      toast.error('Erro ao salvar configurações');
    }
  };

  // ==============================================
  // FUNÇÕES DE EDIÇÃO MANUAL
  // ==============================================

  const editarAvaliacao = (avaliacao) => {
    setAvaliacaoEditando({...avaliacao});
    setShowEditarAvaliacao(true);
  };

  const salvarEdicaoAvaliacao = async () => {
    if (!avaliacaoEditando) return;
    
    try {
      setSalvandoAvaliacao(true);
      const res = await fetch(`${API_URL}/api/assessment-week/avaliacoes/${avaliacaoEditando.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(avaliacaoEditando)
      });
      
      if (res.ok) {
        toast.success('Avaliação atualizada com sucesso!');
        setShowEditarAvaliacao(false);
        setAvaliacaoEditando(null);
        if (planoAtual?.plano?.id) {
          fetchPlanoDetalhe(planoAtual.plano.id);
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao salvar avaliação');
      }
    } catch (error) {
      console.error('Erro ao salvar avaliação:', error);
      toast.error('Erro ao salvar avaliação');
    } finally {
      setSalvandoAvaliacao(false);
    }
  };

  const deletarAvaliacao = async (avaliacaoId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/avaliacoes/${avaliacaoId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Avaliação excluída!');
        if (planoAtual?.plano?.id) {
          fetchPlanoDetalhe(planoAtual.plano.id);
        }
      } else {
        toast.error('Erro ao excluir avaliação');
      }
    } catch (error) {
      toast.error('Erro ao excluir avaliação');
    }
  };

  const adicionarAvaliacaoManual = async () => {
    if (!planoAtual?.plano?.id) {
      toast.error('Selecione um plano primeiro');
      return;
    }
    
    if (!novaAvaliacao.turma_id || !novaAvaliacao.disciplina_nome || !novaAvaliacao.data_avaliacao) {
      toast.error('Preencha turma, disciplina e data');
      return;
    }
    
    try {
      setSalvandoAvaliacao(true);
      const res = await fetch(`${API_URL}/api/assessment-week/planos/${planoAtual.plano.id}/avaliacoes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(novaAvaliacao)
      });
      
      if (res.ok) {
        toast.success('Avaliação adicionada com sucesso!');
        setShowAdicionarAvaliacao(false);
        setNovaAvaliacao({
          turma_id: '',
          disciplina_nome: '',
          professor_nome: '',
          data_avaliacao: '',
          horario_inicio: '07:30',
          horario_fim: '08:20',
          aula_numero: 1
        });
        fetchPlanoDetalhe(planoAtual.plano.id);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao adicionar avaliação');
      }
    } catch (error) {
      toast.error('Erro ao adicionar avaliação');
    } finally {
      setSalvandoAvaliacao(false);
    }
  };

  const despublicarPlano = async (planoId) => {
    if (!window.confirm('Tem certeza que deseja despublicar este plano? Ele será removido do DNA Connect.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/assessment-week/planos/${planoId}/despublicar`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Plano despublicado do DNA Connect!');
        fetchPlanos();
        fetchDashboard();
        if (planoAtual?.plano?.id === planoId) {
          fetchPlanoDetalhe(planoId);
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao despublicar');
      }
    } catch (error) {
      toast.error('Erro ao despublicar plano');
    }
  };

  const fetchPlanoDetalhe = useCallback(async (planId) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/assessment-week/planos/${planId}`);
      const data = await res.json();
      setPlanoAtual(data);
      setActiveTab('previa');
    } catch (error) {
      console.error('Erro ao carregar plano:', error);
      toast.error('Erro ao carregar detalhes do plano');
    } finally {
      setLoading(false);
    }
  }, []);

  // ==============================================
  // GERAR SEMANA
  // ==============================================

  const gerarSemana = async () => {
    if (!geracaoForm.data_inicio || !geracaoForm.data_fim) {
      toast.error('Selecione as datas do período');
      return;
    }
    
    if (!geracaoForm.gerar_todas_turmas && geracaoForm.turma_ids.length === 0) {
      toast.error('Selecione pelo menos uma turma');
      return;
    }

    try {
      setGerando(true);
      const res = await fetch(`${API_URL}/api/assessment-week/gerar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(geracaoForm)
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Semana gerada! ${data.diagnostico.total_avaliacoes} avaliações criadas.`);
        setShowNovaGeracao(false);
        fetchPlanos();
        fetchDashboard();
        fetchConflitos();
        
        // Carregar plano recém criado
        if (data.plan_id) {
          fetchPlanoDetalhe(data.plan_id);
        }
      } else {
        toast.error(data.detail || 'Erro ao gerar semana');
      }
    } catch (error) {
      console.error('Erro ao gerar:', error);
      toast.error('Erro ao gerar semana de avaliações');
    } finally {
      setGerando(false);
    }
  };

  // ==============================================
  // PUBLICAR
  // ==============================================

  const publicarPlano = async () => {
    if (!planoAtual?.plano?.id) return;

    try {
      setPublicando(true);
      const res = await fetch(`${API_URL}/api/assessment-week/publicar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planoAtual.plano.id,
          publicar_todas: true
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        if (data.publicacoes > 0) {
          toast.success(`Publicado para ${data.publicacoes} turma(s): ${data.turmas_publicadas?.join(', ')}`);
        } else {
          toast.warning('Nenhuma turma publicada. Verifique se existem avaliações geradas para as turmas do plano.');
        }
        
        // Mostrar alerta se houve turmas sem avaliações
        if (data.turmas_sem_avaliacoes?.length > 0) {
          console.warn('Turmas sem avaliações:', data.turmas_sem_avaliacoes);
        }
        
        setShowPublicar(false);
        fetchPlanoDetalhe(planoAtual.plano.id);
        fetchDashboard();
      } else {
        toast.error(data.detail || 'Erro ao publicar');
      }
    } catch (error) {
      console.error('Erro ao publicar:', error);
      toast.error('Erro ao publicar no DNA Connect');
    } finally {
      setPublicando(false);
    }
  };

  const deletarPlano = async (planId) => {
    if (!window.confirm('Tem certeza que deseja deletar este plano?')) return;

    try {
      const res = await fetch(`${API_URL}/api/assessment-week/planos/${planId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Plano deletado');
        fetchPlanos();
        fetchDashboard();
        fetchConflitos();
        if (planoAtual?.plano?.id === planId) {
          setPlanoAtual(null);
          setActiveTab('dashboard');
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erro ao deletar plano');
      }
    } catch (error) {
      toast.error('Erro ao deletar plano');
    }
  };

  const deletarTodosPlanos = async () => {
    const planosNaoPublicados = planos.filter(p => p.status !== 'publicado');
    
    if (planosNaoPublicados.length === 0) {
      toast.info('Não há planos para deletar (planos publicados não podem ser deletados)');
      return;
    }
    
    if (!window.confirm(`Tem certeza que deseja deletar TODOS os ${planosNaoPublicados.length} planos não publicados?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/assessment-week/planos`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchPlanos();
        fetchDashboard();
        fetchConflitos();
        setPlanoAtual(null);
      } else {
        toast.error(data.detail || 'Erro ao deletar planos');
      }
    } catch (error) {
      toast.error('Erro ao deletar planos');
    }
  };

  // ==============================================
  // EFFECTS
  // ==============================================

  useEffect(() => {
    fetchDashboard();
    fetchPlanos();
    fetchTurmas();
    fetchConflitos();
    fetchRegras();
    fetchTodasTurmas();
  }, [fetchDashboard, fetchPlanos, fetchTurmas, fetchConflitos, fetchRegras, fetchTodasTurmas]);

  // ==============================================
  // RENDER
  // ==============================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50/30">
      {/* Header Premium */}
      <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                <Brain className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                  Agente IA de Semana de Avaliações
                  <Sparkles className="h-6 w-6 text-yellow-300" />
                </h1>
                <p className="text-purple-200 text-sm md:text-base mt-1">
                  Planejamento pedagógico inteligente e automatizado
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowNovaGeracao(true)}
                className="bg-white text-purple-700 hover:bg-purple-50 font-semibold shadow-lg"
              >
                <Zap className="h-4 w-4 mr-2" />
                Gerar Semana Inteligente
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm border p-1 rounded-xl">
            <TabsTrigger value="dashboard" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="planos" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
              <Calendar className="h-4 w-4 mr-2" />
              Planos
            </TabsTrigger>
            <TabsTrigger value="previa" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700" disabled={!planoAtual}>
              <Eye className="h-4 w-4 mr-2" />
              Prévia
            </TabsTrigger>
            <TabsTrigger value="conflitos" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Conflitos
              {conflitos.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">{conflitos.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="config" className="rounded-lg data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
              <Settings className="h-4 w-4 mr-2" />
              Configurações
            </TabsTrigger>
          </TabsList>

          {/* TAB: Dashboard */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Cards de Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Planos Criados" 
                value={dashboard?.estatisticas?.total_planos || 0}
                icon={FileText}
                color="purple"
              />
              <StatCard 
                title="Publicados" 
                value={dashboard?.estatisticas?.planos_publicados || 0}
                icon={Send}
                color="green"
              />
              <StatCard 
                title="Em Rascunho" 
                value={dashboard?.estatisticas?.planos_rascunho || 0}
                icon={Clock}
                color="yellow"
              />
              <StatCard 
                title="Conflitos Pendentes" 
                value={dashboard?.estatisticas?.conflitos_pendentes || 0}
                icon={AlertTriangle}
                color="red"
              />
            </div>

            {/* Cards de Trimestres */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((tri) => (
                <Card key={tri} className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">{tri}º Trimestre</p>
                        <h4 className="text-xl font-bold">
                          {dashboard?.planos_por_trimestre?.[`trimestre_${tri}`] || 0} planos
                        </h4>
                      </div>
                      <Calendar className="h-8 w-8 text-purple-300" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Ações Rápidas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  Ações Rápidas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col gap-2 hover:bg-purple-50 hover:border-purple-300"
                    onClick={() => setShowNovaGeracao(true)}
                  >
                    <Brain className="h-6 w-6 text-purple-600" />
                    <span className="text-sm">Nova Geração</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col gap-2 hover:bg-green-50 hover:border-green-300"
                    onClick={() => setActiveTab('planos')}
                  >
                    <Calendar className="h-6 w-6 text-green-600" />
                    <span className="text-sm">Ver Calendário</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col gap-2 hover:bg-yellow-50 hover:border-yellow-300"
                    onClick={() => setActiveTab('conflitos')}
                  >
                    <AlertTriangle className="h-6 w-6 text-yellow-600" />
                    <span className="text-sm">Revisar Conflitos</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col gap-2 hover:bg-blue-50 hover:border-blue-300"
                    onClick={() => setActiveTab('config')}
                  >
                    <Settings className="h-6 w-6 text-blue-600" />
                    <span className="text-sm">Configurações</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col gap-2 hover:bg-pink-50 hover:border-pink-300"
                    onClick={() => {
                      // Pre-select current plan if exists
                      if (planoAtual?.plano?.id) {
                        setPlanosSelecionadosExportar(new Set([planoAtual.plano.id]));
                        setExportarTrimestreFiltro(String(planoAtual.plano.trimestre || ''));
                      }
                      setShowExportarDialog(true);
                    }}
                  >
                    <Download className="h-6 w-6 text-pink-600" />
                    <span className="text-sm">Exportar/Imprimir</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="flex flex-col items-center gap-1 h-auto py-3 px-4"
                    onClick={() => navigate('/semana-avaliacoes/relatorio-impressao')}
                  >
                    <Printer className="h-6 w-6 text-purple-600" />
                    <span className="text-sm">Relatório Impressão</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col gap-2 hover:bg-emerald-50 hover:border-emerald-400"
                    onClick={() => {
                      // Pré-seleciona ano/trimestre com base no plano atual, se existir
                      if (planoAtual?.plano) {
                        setClassroomFiltroAno(planoAtual.plano.ano || new Date().getFullYear());
                        setClassroomFiltroTri(planoAtual.plano.trimestre || 1);
                      }
                      setClassroomResults(null);
                      setClassroomForceReplace(false);
                      setShowClassroomDialog(true);
                    }}
                    data-testid="btn-enviar-classroom"
                  >
                    <GraduationCap className="h-6 w-6 text-emerald-600" />
                    <span className="text-sm">Enviar ao Classroom</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Último Plano */}
            {dashboard?.ultimo_plano && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-gray-600" />
                    Último Plano Gerado
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{dashboard.ultimo_plano.trimestre}º Trimestre {dashboard.ultimo_plano.ano}</p>
                      <p className="text-sm text-gray-500">
                        {dashboard.ultimo_plano.data_inicio} a {dashboard.ultimo_plano.data_fim}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={dashboard.ultimo_plano.status} />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => fetchPlanoDetalhe(dashboard.ultimo_plano.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TAB: Planos */}
          <TabsContent value="planos" className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <h2 className="text-xl font-semibold">Planos de Semana de Avaliações</h2>
              <div className="flex gap-2">
                {planos.length > 0 && planos.some(p => p.status !== 'publicado') && (
                  <Button 
                    variant="outline" 
                    className="text-red-600 hover:bg-red-50 border-red-200"
                    onClick={deletarTodosPlanos}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir Todos
                  </Button>
                )}
                <Button onClick={() => { fetchPlanos(); toast.info('Lista atualizada'); }}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </div>

            {planos.length === 0 ? (
              <Card className="py-12 text-center">
                <CardContent>
                  <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Nenhum plano criado</h3>
                  <p className="text-gray-500 mb-4">Clique em "Gerar Semana Inteligente" para começar</p>
                  <Button onClick={() => setShowNovaGeracao(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Plano
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {planos.map((plano) => (
                  <Card key={plano.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">
                              {plano.trimestre}º Trimestre {plano.ano}
                              {(() => {
                                const names = plano.turma_names?.length > 0
                                  ? plano.turma_names
                                  : (plano.turma_ids || []).map(tid => todasTurmas.find(t => t.id === tid)?.name).filter(Boolean);
                                return names.length > 0 ? (
                                  <span className="text-purple-600 ml-2 text-base font-medium">— {names.join(', ')}</span>
                                ) : null;
                              })()}
                            </h3>
                            <StatusBadge status={plano.status} />
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {plano.data_inicio} a {plano.data_fim}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {plano.turma_ids?.length || 0} turmas
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Versão {plano.versao || 1}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => fetchPlanoDetalhe(plano.id)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Ver
                          </Button>
                          {plano.status !== 'publicado' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => deletarPlano(plano.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB: Prévia */}
          <TabsContent value="previa" className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : planoAtual ? (
              <>
                {/* Header do Plano */}
                <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h2 className="text-2xl font-bold">
                          Semana de Avaliações - {planoAtual.plano.trimestre}º Trimestre
                        </h2>
                        <p className="text-purple-200 mt-1">
                          {planoAtual.plano.data_inicio} a {planoAtual.plano.data_fim}
                        </p>
                        <div className="flex gap-4 mt-3">
                          <span className="flex items-center gap-1 text-sm">
                            <CheckCircle2 className="h-4 w-4" />
                            {planoAtual.total_avaliacoes} avaliações
                          </span>
                          <span className="flex items-center gap-1 text-sm">
                            <AlertTriangle className="h-4 w-4" />
                            {planoAtual.total_conflitos} conflitos
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          variant="outline"
                          className="bg-white/10 text-white border-white/30 hover:bg-white/20"
                          onClick={() => setShowAdicionarAvaliacao(true)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Adicionar Avaliação
                        </Button>
                        {planoAtual.plano.status !== 'publicado' ? (
                          <Button 
                            className="bg-white text-purple-700 hover:bg-purple-50"
                            onClick={() => setShowPublicar(true)}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Publicar no DNA Connect
                          </Button>
                        ) : (
                          <>
                            <Badge className="bg-green-500 text-white py-2 px-4">
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Publicado
                            </Badge>
                            <Button 
                              variant="outline"
                              className="bg-red-500/20 text-white border-red-300 hover:bg-red-500/30"
                              onClick={() => despublicarPlano(planoAtual.plano.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Despublicar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Grid de Avaliações por Data */}
                {/* NOVA VISUALIZAÇÃO: Grade por Turma */}
                <GradeAvaliacoesPorTurma 
                  plano={planoAtual.plano}
                  avaliacoes={planoAtual.avaliacoes || []}
                  porData={planoAtual.por_data || {}}
                  onEditarAvaliacao={editarAvaliacao}
                  onDeletarAvaliacao={deletarAvaliacao}
                  onRefresh={() => fetchPlanoDetalhe(planoAtual.plano.id)}
                />

                {/* Explicações da IA */}
                {planoAtual.avaliacoes?.some(a => a.explicacao_ia) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-600" />
                        Explicações da IA
                      </CardTitle>
                      <CardDescription>
                        Entenda as decisões do algoritmo de distribuição
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {planoAtual.avaliacoes.slice(0, 5).filter(a => a.explicacao_ia).map((aval, i) => (
                          <div key={i} className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                            <p className="text-sm text-gray-700">{aval.explicacao_ia}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="py-12 text-center">
                <CardContent>
                  <Eye className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Nenhum plano selecionado</h3>
                  <p className="text-gray-500">Selecione um plano na aba "Planos" para visualizar</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TAB: Conflitos */}
          <TabsContent value="conflitos" className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h2 className="text-xl font-semibold">Conflitos Pendentes</h2>
              <div className="flex gap-2">
                {conflitos.length > 0 && (
                  <Button 
                    variant="outline" 
                    className="text-red-600 hover:bg-red-50 border-red-200"
                    onClick={excluirTodosConflitos}
                    data-testid="excluir-todos-conflitos-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir Todos
                  </Button>
                )}
                <Button variant="outline" onClick={fetchConflitos} data-testid="atualizar-conflitos-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </div>

            {conflitos.length === 0 ? (
              <Card className="py-12 text-center">
                <CardContent>
                  <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Nenhum conflito pendente!</h3>
                  <p className="text-gray-500">Todos os planos estão validados</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {conflitos.map((conflito) => (
                  <Card key={conflito.id} className="border-l-4 border-l-red-400" data-testid={`conflito-card-${conflito.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <ConflitoBadge severidade={conflito.severidade} />
                            <span className="font-medium">{conflito.tipo_conflito?.replace(/_/g, ' ')}</span>
                          </div>
                          <p className="text-gray-700">{conflito.descricao}</p>
                          {conflito.turma_nome && (
                            <p className="text-sm text-gray-500 mt-1">
                              Turma: {conflito.turma_nome}
                              {conflito.disciplina_nome && ` | Disciplina: ${conflito.disciplina_nome}`}
                            </p>
                          )}
                          {conflito.recomendacao && (
                            <p className="text-sm text-blue-600 mt-2 flex items-center gap-1">
                              <Info className="h-4 w-4" />
                              {conflito.recomendacao}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-yellow-600 hover:bg-yellow-50 border-yellow-200"
                            onClick={() => ignorarConflito(conflito.id)}
                            title="Ignorar este conflito"
                            data-testid={`ignorar-conflito-${conflito.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Ignorar
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            onClick={() => excluirConflito(conflito.id)}
                            title="Excluir permanentemente"
                            data-testid={`excluir-conflito-${conflito.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB: Configurações */}
          <TabsContent value="config" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Regras Pedagógicas
                </CardTitle>
                <CardDescription>
                  Configure as regras para geração automática da semana de avaliações
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {regras && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Máximo de avaliações por dia</Label>
                        <Input 
                          type="number" 
                          value={regras.max_avaliacoes_por_dia} 
                          min={1} 
                          max={6}
                          onChange={(e) => setRegras({...regras, max_avaliacoes_por_dia: parseInt(e.target.value) || 2})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Máximo de pesadas por dia</Label>
                        <Input 
                          type="number" 
                          value={regras.max_pesadas_por_dia} 
                          min={1} 
                          max={3}
                          onChange={(e) => setRegras({...regras, max_pesadas_por_dia: parseInt(e.target.value) || 1})}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Incluir Artes</Label>
                          <p className="text-sm text-gray-500">Incluir disciplinas de Artes nas avaliações</p>
                        </div>
                        <Switch 
                          checked={regras.incluir_artes} 
                          onCheckedChange={(v) => setRegras({...regras, incluir_artes: v})}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Incluir Educação Física</Label>
                          <p className="text-sm text-gray-500">Incluir Educação Física nas avaliações</p>
                        </div>
                        <Switch 
                          checked={regras.incluir_ed_fisica} 
                          onCheckedChange={(v) => setRegras({...regras, incluir_ed_fisica: v})}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Permitir leves com pesadas</Label>
                          <p className="text-sm text-gray-500">Permite avaliações leves no mesmo dia de pesadas</p>
                        </div>
                        <Switch 
                          checked={regras.permitir_leves_com_pesadas} 
                          onCheckedChange={(v) => setRegras({...regras, permitir_leves_com_pesadas: v})}
                        />
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t flex justify-end">
                      <Button onClick={salvarRegras} className="bg-purple-600 hover:bg-purple-700">
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Salvar Configurações
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal: Nova Geração */}
      <Dialog open={showNovaGeracao} onOpenChange={setShowNovaGeracao}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              Gerar Semana de Avaliações Inteligente
            </DialogTitle>
            <DialogDescription>
              Configure os parâmetros para a geração automática
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Trimestre</Label>
                <Select 
                  value={String(geracaoForm.trimestre)} 
                  onValueChange={(v) => setGeracaoForm({...geracaoForm, trimestre: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1º Trimestre</SelectItem>
                    <SelectItem value="2">2º Trimestre</SelectItem>
                    <SelectItem value="3">3º Trimestre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Modo de Geração</Label>
                <Select 
                  value={geracaoForm.modo} 
                  onValueChange={(v) => setGeracaoForm({...geracaoForm, modo: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equilibrado">Equilibrado</SelectItem>
                    <SelectItem value="conservador">Conservador</SelectItem>
                    <SelectItem value="intensivo">Intensivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inicial</Label>
                <Input 
                  type="date" 
                  value={geracaoForm.data_inicio}
                  onChange={(e) => setGeracaoForm({...geracaoForm, data_inicio: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Data Final</Label>
                <Input 
                  type="date" 
                  value={geracaoForm.data_fim}
                  onChange={(e) => setGeracaoForm({...geracaoForm, data_fim: e.target.value})}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
              <Switch 
                checked={geracaoForm.gerar_todas_turmas}
                onCheckedChange={(v) => setGeracaoForm({...geracaoForm, gerar_todas_turmas: v, turma_ids: v ? [] : geracaoForm.turma_ids})}
              />
              <Label className="cursor-pointer">Gerar para TODAS as turmas</Label>
            </div>
            
            {/* Seleção Manual de Turmas */}
            {!geracaoForm.gerar_todas_turmas && (
              <div className="space-y-3 border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Selecione as turmas</Label>
                  <Badge variant="outline" className="text-xs">
                    {geracaoForm.turma_ids.length} selecionada(s)
                  </Badge>
                </div>
                
                <ScrollArea className="h-[200px] pr-4">
                  {turmas.por_segmento && Object.entries(turmas.por_segmento).map(([segmento, turmasSegmento]) => (
                    <div key={segmento} className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox
                          id={`seg-${segmento}`}
                          checked={turmasSegmento.every(t => geracaoForm.turma_ids.includes(t.id))}
                          onCheckedChange={(checked) => {
                            const segmentoIds = turmasSegmento.map(t => t.id);
                            if (checked) {
                              setGeracaoForm({
                                ...geracaoForm,
                                turma_ids: [...new Set([...geracaoForm.turma_ids, ...segmentoIds])]
                              });
                            } else {
                              setGeracaoForm({
                                ...geracaoForm,
                                turma_ids: geracaoForm.turma_ids.filter(id => !segmentoIds.includes(id))
                              });
                            }
                          }}
                        />
                        <Label 
                          htmlFor={`seg-${segmento}`}
                          className="text-sm font-semibold text-gray-700 cursor-pointer"
                        >
                          {segmento} ({turmasSegmento.length})
                        </Label>
                      </div>
                      <div className="grid grid-cols-2 gap-2 ml-6">
                        {turmasSegmento.map(turma => (
                          <div key={turma.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`turma-${turma.id}`}
                              checked={geracaoForm.turma_ids.includes(turma.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setGeracaoForm({
                                    ...geracaoForm,
                                    turma_ids: [...geracaoForm.turma_ids, turma.id]
                                  });
                                } else {
                                  setGeracaoForm({
                                    ...geracaoForm,
                                    turma_ids: geracaoForm.turma_ids.filter(id => id !== turma.id)
                                  });
                                }
                              }}
                            />
                            <Label 
                              htmlFor={`turma-${turma.id}`}
                              className="text-sm cursor-pointer"
                            >
                              {turma.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </ScrollArea>
                
                {geracaoForm.turma_ids.length === 0 && (
                  <p className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" />
                    Selecione pelo menos uma turma
                  </p>
                )}
              </div>
            )}
            
            {/* Seleção de Disciplinas por Turma */}
            {!geracaoForm.gerar_todas_turmas && geracaoForm.turma_ids.length > 0 && (
              <div className="space-y-3 border rounded-lg p-4 bg-blue-50/50">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Selecionar Disciplinas</Label>
                    <p className="text-xs text-gray-500">Escolha quais disciplinas terão avaliações</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={geracaoForm.gerar_todas_disciplinas}
                      onCheckedChange={(v) => setGeracaoForm({...geracaoForm, gerar_todas_disciplinas: v})}
                    />
                    <Label className="text-sm">Todas</Label>
                  </div>
                </div>
                
                {!geracaoForm.gerar_todas_disciplinas && (
                  <ScrollArea className="h-[250px] pr-4">
                    {geracaoForm.turma_ids.map(turmaId => {
                      const turma = turmas.turmas?.find(t => t.id === turmaId);
                      const disciplinas = disciplinasPorTurma[turmaId] || [];
                      const selecionadas = geracaoForm.disciplinas_selecionadas[turmaId] || [];
                      
                      // Buscar disciplinas se ainda não temos
                      if (!disciplinasPorTurma[turmaId] && !loadingDisciplinas) {
                        fetchDisciplinasTurma(turmaId);
                      }
                      
                      return (
                        <div key={turmaId} className="mb-4 p-3 bg-white rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="font-semibold text-purple-700">
                              {turma?.name || 'Turma'}
                            </Label>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setGeracaoForm(prev => ({
                                    ...prev,
                                    disciplinas_selecionadas: {
                                      ...prev.disciplinas_selecionadas,
                                      [turmaId]: disciplinas.map(d => d.nome)
                                    }
                                  }));
                                }}
                              >
                                Todas
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setGeracaoForm(prev => ({
                                    ...prev,
                                    disciplinas_selecionadas: {
                                      ...prev.disciplinas_selecionadas,
                                      [turmaId]: []
                                    }
                                  }));
                                }}
                              >
                                Nenhuma
                              </Button>
                            </div>
                          </div>
                          
                          {loadingDisciplinas && !disciplinas.length ? (
                            <div className="flex items-center gap-2 text-gray-500 text-sm">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Carregando disciplinas...
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {disciplinas.map(disc => (
                                <div key={disc.nome} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`disc-${turmaId}-${disc.nome}`}
                                    checked={selecionadas.includes(disc.nome)}
                                    onCheckedChange={(checked) => {
                                      setGeracaoForm(prev => {
                                        const atual = prev.disciplinas_selecionadas[turmaId] || [];
                                        const novas = checked 
                                          ? [...atual, disc.nome]
                                          : atual.filter(d => d !== disc.nome);
                                        return {
                                          ...prev,
                                          disciplinas_selecionadas: {
                                            ...prev.disciplinas_selecionadas,
                                            [turmaId]: novas
                                          }
                                        };
                                      });
                                    }}
                                  />
                                  <Label 
                                    htmlFor={`disc-${turmaId}-${disc.nome}`}
                                    className="text-sm cursor-pointer flex items-center gap-1"
                                  >
                                    {disc.nome}
                                    <span className="text-xs text-gray-400">({disc.aulas_semana}x)</span>
                                  </Label>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="mt-2 text-xs text-gray-500">
                            {selecionadas.length} de {disciplinas.length} disciplinas selecionadas
                          </div>
                        </div>
                      );
                    })}
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowNovaGeracao(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={gerarSemana} 
              disabled={gerando}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {gerando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Gerar Semana Inteligente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Publicar */}
      <Dialog open={showPublicar} onOpenChange={setShowPublicar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-green-600" />
              Publicar no DNA Connect
            </DialogTitle>
            <DialogDescription>
              A semana de avaliações será publicada para as famílias visualizarem
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-green-800">
                <strong>{planoAtual?.total_avaliacoes || 0}</strong> avaliações serão publicadas para{' '}
                <strong>{planoAtual?.plano?.turma_ids?.length || 0}</strong> turmas.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowPublicar(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={publicarPlano} 
              disabled={publicando}
              className="bg-green-600 hover:bg-green-700"
            >
              {publicando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Publicar Agora
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Editar Avaliação */}
      <Dialog open={showEditarAvaliacao} onOpenChange={setShowEditarAvaliacao}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              Editar Avaliação
            </DialogTitle>
            <DialogDescription>
              Altere os dados da avaliação manualmente
            </DialogDescription>
          </DialogHeader>
          
          {avaliacaoEditando && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Disciplina</Label>
                <Input 
                  value={avaliacaoEditando.disciplina_nome || ''} 
                  onChange={(e) => setAvaliacaoEditando({...avaliacaoEditando, disciplina_nome: e.target.value})}
                />
              </div>
              <div>
                <Label>Professor</Label>
                <Input 
                  value={avaliacaoEditando.professor_nome || ''} 
                  onChange={(e) => setAvaliacaoEditando({...avaliacaoEditando, professor_nome: e.target.value})}
                />
              </div>
              <div>
                <Label>Data</Label>
                <Input 
                  type="date"
                  value={avaliacaoEditando.data_avaliacao || ''} 
                  onChange={(e) => setAvaliacaoEditando({...avaliacaoEditando, data_avaliacao: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Horário Início</Label>
                  <Input 
                    type="time"
                    value={avaliacaoEditando.horario_inicio || ''} 
                    onChange={(e) => setAvaliacaoEditando({...avaliacaoEditando, horario_inicio: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Horário Fim</Label>
                  <Input 
                    type="time"
                    value={avaliacaoEditando.horario_fim || ''} 
                    onChange={(e) => setAvaliacaoEditando({...avaliacaoEditando, horario_fim: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Nº da Aula</Label>
                <Input 
                  type="number"
                  min="1"
                  value={avaliacaoEditando.aula_numero || 1} 
                  onChange={(e) => setAvaliacaoEditando({...avaliacaoEditando, aula_numero: parseInt(e.target.value)})}
                />
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowEditarAvaliacao(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={salvarEdicaoAvaliacao} 
              disabled={salvandoAvaliacao}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {salvandoAvaliacao ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Adicionar Avaliação Manual */}
      <Dialog open={showAdicionarAvaliacao} onOpenChange={setShowAdicionarAvaliacao}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Adicionar Avaliação Manual
            </DialogTitle>
            <DialogDescription>
              Adicione uma avaliação manualmente ao plano
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Turma *</Label>
              <Select 
                value={novaAvaliacao.turma_id}
                onValueChange={(v) => setNovaAvaliacao({...novaAvaliacao, turma_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a turma" />
                </SelectTrigger>
                <SelectContent>
                  {turmas?.turmas?.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Disciplina *</Label>
              <Input 
                value={novaAvaliacao.disciplina_nome} 
                onChange={(e) => setNovaAvaliacao({...novaAvaliacao, disciplina_nome: e.target.value})}
                placeholder="Ex: Matemática"
              />
            </div>
            <div>
              <Label>Professor</Label>
              <Input 
                value={novaAvaliacao.professor_nome} 
                onChange={(e) => setNovaAvaliacao({...novaAvaliacao, professor_nome: e.target.value})}
                placeholder="Ex: Prof. João"
              />
            </div>
            <div>
              <Label>Data *</Label>
              <Input 
                type="date"
                value={novaAvaliacao.data_avaliacao} 
                onChange={(e) => setNovaAvaliacao({...novaAvaliacao, data_avaliacao: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Horário Início</Label>
                <Input 
                  type="time"
                  value={novaAvaliacao.horario_inicio} 
                  onChange={(e) => setNovaAvaliacao({...novaAvaliacao, horario_inicio: e.target.value})}
                />
              </div>
              <div>
                <Label>Horário Fim</Label>
                <Input 
                  type="time"
                  value={novaAvaliacao.horario_fim} 
                  onChange={(e) => setNovaAvaliacao({...novaAvaliacao, horario_fim: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Nº da Aula</Label>
              <Input 
                type="number"
                min="1"
                value={novaAvaliacao.aula_numero} 
                onChange={(e) => setNovaAvaliacao({...novaAvaliacao, aula_numero: parseInt(e.target.value) || 1})}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowAdicionarAvaliacao(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={adicionarAvaliacaoManual} 
              disabled={salvandoAvaliacao}
              className="bg-green-600 hover:bg-green-700"
            >
              {salvandoAvaliacao ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Adicionar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Exportar/Imprimir */}
      <Dialog open={showExportarDialog} onOpenChange={setShowExportarDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-pink-600" />
              Exportar / Imprimir Semana de Avaliações
            </DialogTitle>
            <DialogDescription>
              Escolha as semanas, formato e turmas para gerar a visualização de impressão
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Seleção de Semanas (Planos) por Trimestre */}
            <div>
              <p className="text-sm font-medium mb-2">Semanas:</p>
              {/* Filtro por trimestre */}
              <div className="flex gap-2 mb-3">
                <Button
                  variant={exportarTrimestreFiltro === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setExportarTrimestreFiltro('')}
                  className={exportarTrimestreFiltro === '' ? 'bg-purple-600' : ''}
                >
                  Todos
                </Button>
                {[1, 2, 3].map(tri => (
                  <Button
                    key={tri}
                    variant={exportarTrimestreFiltro === String(tri) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExportarTrimestreFiltro(String(tri))}
                    className={exportarTrimestreFiltro === String(tri) ? 'bg-purple-600' : ''}
                  >
                    {tri}º Tri
                  </Button>
                ))}
              </div>
              {/* Lista de planos como checkboxes */}
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-gray-500">{planosSelecionadosExportar.size} semana(s) selecionada(s)</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => {
                      const filtered = planos.filter(p => !exportarTrimestreFiltro || String(p.trimestre) === exportarTrimestreFiltro);
                      setPlanosSelecionadosExportar(new Set(filtered.map(p => p.id)));
                    }}>Selecionar Todas</Button>
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setPlanosSelecionadosExportar(new Set())}>Limpar</Button>
                  </div>
                </div>
                <div className="space-y-1">
                  {planos
                    .filter(p => !exportarTrimestreFiltro || String(p.trimestre) === exportarTrimestreFiltro)
                    .sort((a, b) => (a.data_inicio || '').localeCompare(b.data_inicio || ''))
                    .map(p => (
                    <label key={p.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-sm">
                      <Checkbox
                        checked={planosSelecionadosExportar.has(p.id)}
                        onCheckedChange={(checked) => {
                          const novo = new Set(planosSelecionadosExportar);
                          if (checked) novo.add(p.id); else novo.delete(p.id);
                          setPlanosSelecionadosExportar(novo);
                        }}
                      />
                      <span className="flex-1">
                        <span className="font-medium">{p.trimestre}º Tri</span>
                        <span className="text-gray-500 ml-2">{p.data_inicio} — {p.data_fim}</span>
                        {(() => {
                          const names = p.turma_names?.length > 0
                            ? p.turma_names
                            : (p.turma_ids || []).map(tid => todasTurmas.find(t => t.id === tid)?.name).filter(Boolean);
                          return names.length > 0 ? <span className="text-purple-600 ml-2 text-xs">({names.join(', ')})</span> : null;
                        })()}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'publicado' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {p.status || 'rascunho'}
                      </span>
                    </label>
                  ))}
                  {planos.filter(p => !exportarTrimestreFiltro || String(p.trimestre) === exportarTrimestreFiltro).length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-3">Nenhum plano encontrado</p>
                  )}
                </div>
              </div>
            </div>

            {/* Formato de impressão */}
            <div>
              <p className="text-sm font-medium mb-2">Formato de impressão:</p>
              <div className="flex gap-4">
                <Button
                  variant={formatoImpressao === 'lista' ? 'default' : 'outline'}
                  onClick={() => setFormatoImpressao('lista')}
                  className={formatoImpressao === 'lista' ? 'bg-blue-600' : ''}
                >
                  <List className="h-4 w-4 mr-2" />
                  Lista
                </Button>
                <Button
                  variant={formatoImpressao === 'grade' ? 'default' : 'outline'}
                  onClick={() => setFormatoImpressao('grade')}
                  className={formatoImpressao === 'grade' ? 'bg-blue-600' : ''}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Grade/Quadro
                </Button>
              </div>
            </div>
            
            {/* Seleção de tipo */}
            <div>
              <p className="text-sm font-medium mb-2">Turmas:</p>
              <div className="flex gap-4">
                <Button
                  variant={exportarTipo === 'todas' ? 'default' : 'outline'}
                  onClick={() => setExportarTipo('todas')}
                  className={exportarTipo === 'todas' ? 'bg-purple-600' : ''}
                >
                  Todas do Plano
                </Button>
                <Button
                  variant={exportarTipo === 'selecionadas' ? 'default' : 'outline'}
                  onClick={() => setExportarTipo('selecionadas')}
                  className={exportarTipo === 'selecionadas' ? 'bg-purple-600' : ''}
                >
                  Selecionar Turmas
                </Button>
              </div>
            </div>
            
            {/* Lista de turmas para seleção */}
            {exportarTipo === 'selecionadas' && (
              <div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-sm text-gray-500">Selecione as turmas:</p>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const todas = new Set(todasTurmas.map(t => t.id));
                        setTurmasSelecionadasExportar(todas);
                      }}
                    >
                      Selecionar Todas
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setTurmasSelecionadasExportar(new Set())}
                    >
                      Limpar
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {todasTurmas.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(turma => (
                    <label key={turma.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-sm">
                      <Checkbox
                        checked={turmasSelecionadasExportar.has(turma.id)}
                        onCheckedChange={(checked) => {
                          const novo = new Set(turmasSelecionadasExportar);
                          if (checked) {
                            novo.add(turma.id);
                          } else {
                            novo.delete(turma.id);
                          }
                          setTurmasSelecionadasExportar(novo);
                        }}
                      />
                      <span>{turma.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {planos.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                <p>Nenhum plano criado ainda</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowExportarDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                const turmaIds = exportarTipo === 'todas' 
                  ? 'todas' 
                  : Array.from(turmasSelecionadasExportar).join(',');
                
                if (exportarTipo === 'selecionadas' && turmasSelecionadasExportar.size === 0) {
                  toast.error('Selecione pelo menos uma turma');
                  return;
                }

                // Use selected plans or current plan
                const planosParaImprimir = planosSelecionadosExportar.size > 0
                  ? Array.from(planosSelecionadosExportar)
                  : planoAtual?.plano?.id ? [planoAtual.plano.id] : [];

                if (planosParaImprimir.length === 0) {
                  toast.error('Selecione pelo menos uma semana');
                  return;
                }

                // Open print page for each selected plan
                planosParaImprimir.forEach((planId, index) => {
                  setTimeout(() => {
                    const printUrl = `${API_URL}/api/assessment-week/imprimir/${planId}?turmas=${turmaIds}&formato=${formatoImpressao}`;
                    window.open(printUrl, '_blank');
                  }, index * 500); // Small delay to avoid popup blocker
                });

                setShowExportarDialog(false);
              }}
              disabled={planosSelecionadosExportar.size === 0 && !planoAtual?.plano?.id}
              className="bg-pink-600 hover:bg-pink-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Gerar Impressão ({planosSelecionadosExportar.size || 1} semana{(planosSelecionadosExportar.size || 1) > 1 ? 's' : ''})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* V4.93 · Modal Publicar no Google Classroom */}
      <Dialog open={showClassroomDialog} onOpenChange={(v) => { if (!v && !classroomPublishing) setShowClassroomDialog(false); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="classroom-publish-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-emerald-600" />
              Publicar cronogramas no Classroom
            </DialogTitle>
            <DialogDescription>
              Uma publicação por turma no Mural do Google Classroom. Fonte: planos publicados na Semana de Avaliações.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-3 py-2">
            <div>
              <Label className="text-xs">Ano</Label>
              <Select value={String(classroomFiltroAno)} onValueChange={(v) => setClassroomFiltroAno(parseInt(v, 10))}>
                <SelectTrigger data-testid="classroom-filter-ano"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Trimestre</Label>
              <Select value={String(classroomFiltroTri)} onValueChange={(v) => setClassroomFiltroTri(parseInt(v, 10))}>
                <SelectTrigger data-testid="classroom-filter-tri"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={loadClassroomPreview} disabled={classroomLoading} className="flex-1" data-testid="classroom-refresh">
                {classroomLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Atualizar</span>
              </Button>
              <Button
                variant="outline"
                onClick={doBulkLink2026}
                disabled={bulkLinking}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                title="Persiste os 30 Course IDs oficiais 2026 em class_groups.google_classroom_course_id"
                data-testid="classroom-bulk-link-2026"
              >
                {bulkLinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />}
                <span className="ml-2 text-xs">Vincular Salas 2026</span>
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto border rounded">
            {classroomLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500 gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando turmas…</div>
            ) : classroomTurmas.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">Nenhuma turma com plano publicado neste ano/trimestre.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 w-8">
                      <Checkbox
                        checked={classroomSelected.size > 0 && classroomSelected.size === classroomTurmas.filter(t => t.classroom_course_id).length}
                        onCheckedChange={(v) => {
                          if (v) setClassroomSelected(new Set(classroomTurmas.filter(t => t.classroom_course_id).map(t => t.turma_id)));
                          else setClassroomSelected(new Set());
                        }}
                        data-testid="classroom-select-all"
                      />
                    </th>
                    <th className="text-left px-3 py-2">Turma</th>
                    <th className="text-center px-3 py-2">Avaliações</th>
                    <th className="text-left px-3 py-2">Sala Classroom</th>
                    <th className="text-center px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {classroomTurmas.map((t) => {
                    const hasLink = !!t.classroom_course_id;
                    const isChecked = classroomSelected.has(t.turma_id);
                    return (
                      <tr key={t.turma_id} className={`border-t hover:bg-slate-50 ${!hasLink ? 'opacity-60' : ''}`} data-testid={`classroom-row-${t.turma_id.slice(0,8)}`}>
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={isChecked}
                            disabled={!hasLink}
                            onCheckedChange={(v) => {
                              const s = new Set(classroomSelected);
                              if (v) s.add(t.turma_id); else s.delete(t.turma_id);
                              setClassroomSelected(s);
                            }}
                            data-testid={`classroom-check-${t.turma_id.slice(0,8)}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">{t.turma_nome}</td>
                        <td className="px-3 py-2 text-center">{t.n_avaliacoes}</td>
                        <td className="px-3 py-2 text-xs">
                          {hasLink ? (
                            <a href={t.classroom_course_link || '#'} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                              vinculada <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> não vinculada</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {t.already_published ? (
                            <a href={t.last_post_link || '#'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200">
                              <CheckCircle2 className="h-3 w-3" /> Já publicado
                            </a>
                          ) : hasLink ? (
                            <span className="text-[11px] text-slate-500">Pronto</span>
                          ) : (
                            <span className="text-[11px] text-amber-700">sem sala</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              id="classroom-force-replace"
              type="checkbox"
              checked={classroomForceReplace}
              onChange={(e) => setClassroomForceReplace(e.target.checked)}
              data-testid="classroom-force-replace"
            />
            <label htmlFor="classroom-force-replace" className="text-xs text-slate-700">
              Republicar cronograma atualizado (para turmas já publicadas)
            </label>
          </div>

          {classroomResults && (
            <div className="bg-slate-50 border rounded p-3 text-xs space-y-1" data-testid="classroom-results">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> {classroomResults.success_count} publicadas</span>
                {classroomResults.no_link_count > 0 && <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {classroomResults.no_link_count} sem sala</span>}
                {classroomResults.already_count > 0 && <span className="inline-flex items-center gap-1 text-blue-700"><Info className="h-3.5 w-3.5" /> {classroomResults.already_count} já publicadas</span>}
                {classroomResults.error_count > 0 && <span className="inline-flex items-center gap-1 text-rose-700"><XCircle className="h-3.5 w-3.5" /> {classroomResults.error_count} falha(s)</span>}
                {classroomResults.error_count > 0 && (
                  <Button size="sm" variant="outline" onClick={doClassroomRetryErrors} disabled={classroomPublishing} className="ml-auto" data-testid="classroom-retry-errors">
                    <RefreshCw className="h-3 w-3 mr-1" /> Tentar novamente as falhas
                  </Button>
                )}
              </div>
              {(classroomResults.results?.errors || []).slice(0, 5).map((e, i) => (
                <div key={i} className="text-rose-700">• {e.turma_nome || e.turma_id}: {e.reason}</div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setShowClassroomDialog(false)} disabled={classroomPublishing}>Fechar</Button>
            <Button
              onClick={doClassroomPublish}
              disabled={classroomPublishing || classroomSelected.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="classroom-publish-btn"
            >
              {classroomPublishing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Publicar {classroomSelected.size} turma{classroomSelected.size !== 1 ? 's' : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
